import React, { useRef, useState, useEffect } from 'react';
import { ProGallery } from 'pro-gallery';
import { GALLERY_CONSTS } from 'pro-gallery-lib';
import { useEnvironment } from '@wix/yoshi-flow-editor';

import type { IMediaItem } from '@wix/social-groups-serverless/dist/media/types';

import { Dialog } from 'wui/Dialog';

import { Thumbnail } from './Thumbnail';
import { useGalleryOptions, useFullscreenOptions } from './theme';

import './ProGallery.global.css';

interface MediaGalleryProps {
  items: IMediaItem[];

  fetchMore?(): void;
}

export function MediaGallery(props: MediaGalleryProps) {
  const { items, fetchMore } = props;

  const { isSSR, isMobile } = useEnvironment();
  const options = useGalleryOptions();
  const fullscreenOptions = useFullscreenOptions();

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(isMobile ? 320 : 980);
  const [activeId, setActiveId] = useState(-1);

  const showFullscreen = !!~activeId;

  useEffect(() => {
    if (isSSR) {
      return;
    }
    const observer = createContainerObserver(containerRef);
    observer.observe();
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef}>
      <ProGallery
        items={items}
        isPrerenderMode={isSSR}
        options={options}
        id="media-gallery"
        eventsListener={handleGalleryEvents}
        container={{
          height: 400,
          width: containerWidth,
        }}
        customComponents={{
          customHoverRenderer: (props) => (
            <Thumbnail createdBy={items[props.idx]?.createdBy} />
          ),
        }}
      />

      {!isSSR && (
        <Dialog fullscreen isOpen={showFullscreen} onClose={closeFullscreen}>
          <ProGallery
            id="fullscreen-galley"
            items={items}
            activeIndex={activeId}
            options={fullscreenOptions}
            container={{
              width: window.innerWidth,
              height: window.innerHeight,
            }}
          />
        </Dialog>
      )}
    </div>
  );

  function closeFullscreen() {
    setActiveId(-1);
  }

  function handleGalleryEvents(eventName: string, data: any) {
    switch (eventName) {
      case GALLERY_CONSTS.events.NEED_MORE_ITEMS:
        fetchMore?.();
        break;
      case GALLERY_CONSTS.events.ITEM_ACTION_TRIGGERED:
        setActiveId(data.idx);
        break;
      default:
    }
  }

  function createContainerObserver(
    containerRef: React.RefObject<HTMLDivElement>,
  ) {
    const container = containerRef.current;

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });

    const observe = () => {
      if (container) {
        observer.observe(container);
      }
    };

    const disconnect = () => observer.disconnect();

    return { observe, disconnect };
  }
}

MediaGallery.displayName = 'MediaGallery';
